// Reports API Client - Uses Analytics API
import analyticsApiClient from './analyticsApiClient';
import type {
  ReportDataRequest,
  ReportDataResponse,
  QueryConfig,
  FilterSpec,
  OrderSpec,
  TimeRange,
  CycleSummaryRow,
  CycleUsersRow,
  GoalProgressDashboardRow,
  GoalCompletionRateRow,
  IndividualGoalSummaryRow,
  GoalSettingComplianceRow,
  ReviewCycleExtendedRow,
  SurveyParticipationRow,
  CheckinFrequencyRow,
  ExportOptions,
} from '../types/reportsApi';
import { getAuthToken } from './apiClient';
import { ANALYTICS_API_BASE_URL } from './analyticsApiClient';

// Analytics API endpoint (relative to analytics base URL)
const REPORTS_ENDPOINT = '/reports/data';

/**
 * Fetch report data from the analytics API
 */
export async function fetchReportData<T = Record<string, unknown>>(
  reportKey: string,
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<T>> {
  const request: ReportDataRequest = {
    report_key: reportKey,
    query_config: queryConfig,
  };

  const response = await analyticsApiClient.post<ReportDataResponse<T>>(REPORTS_ENDPOINT, request);
  return response.data;
}

/**
 * Fetch cycle summary report data
 */
export async function fetchCycleSummary(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<CycleSummaryRow>> {
  return fetchReportData<CycleSummaryRow>('cycle_summary', queryConfig);
}

/**
 * Fetch cycle users report data
 */
export async function fetchCycleUsers(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<CycleUsersRow>> {
  return fetchReportData<CycleUsersRow>('cycle_users', queryConfig);
}

/**
 * Fetch goal progress dashboard report data
 */
export async function fetchGoalProgressDashboard(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<GoalProgressDashboardRow>> {
  return fetchReportData<GoalProgressDashboardRow>('goal_progress_dashboard', queryConfig);
}

/**
 * Fetch goal completion rate report data
 */
export async function fetchGoalCompletionRate(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<GoalCompletionRateRow>> {
  return fetchReportData<GoalCompletionRateRow>('goal_completion_rate', queryConfig);
}

/**
 * Fetch individual goal summary report data
 */
export async function fetchIndividualGoalSummary(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<IndividualGoalSummaryRow>> {
  return fetchReportData<IndividualGoalSummaryRow>('individual_goal_summary', queryConfig);
}

/**
 * Fetch goal setting compliance report data
 */
export async function fetchGoalSettingCompliance(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<GoalSettingComplianceRow>> {
  return fetchReportData<GoalSettingComplianceRow>('goal_setting_compliance', queryConfig);
}

/**
 * Fetch review cycle extended report data
 */
export async function fetchReviewCycleExtended(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<ReviewCycleExtendedRow>> {
  return fetchReportData<ReviewCycleExtendedRow>('review_cycle_extended', queryConfig);
}

/**
 * Fetch survey participation report data
 */
export async function fetchSurveyParticipation(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<SurveyParticipationRow>> {
  return fetchReportData<SurveyParticipationRow>('survey_participation', queryConfig);
}

/**
 * Fetch check-in frequency report data
 */
export async function fetchCheckinFrequency(
  queryConfig?: QueryConfig
): Promise<ReportDataResponse<CheckinFrequencyRow>> {
  return fetchReportData<CheckinFrequencyRow>('checkin_frequency', queryConfig);
}

/**
 * Build query config from grid state
 */
export function buildQueryConfig(options: {
  page?: number;
  pageSize?: number;
  sortModel?: { field: string; sort: 'asc' | 'desc' | null | undefined }[];
  filters?: FilterSpec[];
  timeRange?: TimeRange;
  searchQuery?: string;
  searchFields?: string[];
}): QueryConfig {
  const {
    page = 0,
    pageSize = 100,
    sortModel,
    filters = [],
    timeRange,
    searchQuery,
    searchFields = [],
  } = options;

  const queryConfig: QueryConfig = {
    limit: pageSize,
    offset: page * pageSize,
  };

  // Add sorting
  if (sortModel && sortModel.length > 0) {
    const orderBy: OrderSpec[] = sortModel
      .filter((sort) => sort.sort)
      .map((sort) => ({
        field: sort.field,
        direction: sort.sort as 'asc' | 'desc',
      }));
    if (orderBy.length > 0) {
      queryConfig.order_by = orderBy;
    }
  }

  // Add filters
  const allFilters: FilterSpec[] = [...filters];

  // Add search as LIKE filter on specified fields
  if (searchQuery && searchQuery.trim() && searchFields.length > 0) {
    // For simplicity, search on the first field only
    // In a real implementation, you might want OR logic across multiple fields
    allFilters.push({
      field: searchFields[0],
      operator: 'like',
      value: `%${searchQuery.trim()}%`,
    });
  }

  if (allFilters.length > 0) {
    queryConfig.filters = allFilters;
  }

  // Add time range
  if (timeRange) {
    queryConfig.time_range = timeRange;
  }

  return queryConfig;
}

/**
 * Export report to CSV file
 * Makes a POST request to the export endpoint and triggers a file download
 */
export async function exportReportToCSV(
  reportKey: string,
  options: ExportOptions = {}
): Promise<void> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${ANALYTICS_API_BASE_URL}/reports/${reportKey}/export`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    }
  );

  // Handle errors (returned as JSON before streaming starts)
  if (!response.ok) {
    let errorMessage = 'Export failed';
    try {
      const error = await response.json();
      errorMessage = error.details || error.error || `Export failed with status ${response.status}`;
    } catch {
      errorMessage = `Export failed with status ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  // Extract filename from Content-Disposition header
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition
    ?.match(/filename="(.+)"/)?.[1]
    ?? `${reportKey}-export.csv`;

  // Convert response stream to blob and trigger download
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Hook-ready data fetcher for use with React Query or SWR
 */
export const reportsApi = {
  fetchReportData,
  fetchCycleSummary,
  fetchCycleUsers,
  fetchGoalProgressDashboard,
  fetchGoalCompletionRate,
  fetchIndividualGoalSummary,
  fetchGoalSettingCompliance,
  fetchReviewCycleExtended,
  fetchSurveyParticipation,
  fetchCheckinFrequency,
  buildQueryConfig,
  exportReportToCSV,
};

export default reportsApi;

